import { ReactNode, forwardRef } from 'react';
import { motion, HTMLMotionProps, useReducedMotion } from 'framer-motion';
import { fadeInUp, fadeIn, slideInLeft, slideInRight, viewportSettings } from '@/lib/animations';
import { cn } from '@/lib/utils';

type AnimationType = 'fadeUp' | 'fadeIn' | 'slideLeft' | 'slideRight';

interface AnimatedSectionProps extends Omit<HTMLMotionProps<'section'>, 'children'> {
  children: ReactNode;
  animation?: AnimationType;
  delay?: number;
  className?: string;
  once?: boolean;
}

const animationVariants = {
  fadeUp: fadeInUp,
  fadeIn: fadeIn,
  slideLeft: slideInLeft,
  slideRight: slideInRight,
};

/**
 * Section component with scroll-triggered animations
 * Respects user's reduced motion preferences
 */
export const AnimatedSection = forwardRef<HTMLElement, AnimatedSectionProps>(
  (
    {
      children,
      animation = 'fadeUp',
      delay = 0,
      className,
      once = true,
      ...props
    },
    ref
  ) => {
    const shouldReduceMotion = useReducedMotion();
    const variants = animationVariants[animation];

    // Skip animation if user prefers reduced motion
    if (shouldReduceMotion) {
      return (
        <section ref={ref} className={className} {...(props as React.HTMLAttributes<HTMLElement>)}>
          {children}
        </section>
      );
    }

    return (
      <motion.section
        ref={ref}
        initial="initial"
        whileInView="animate"
        viewport={{ ...viewportSettings, once }}
        variants={variants}
        transition={{ delay }}
        className={cn(className)}
        {...props}
      >
        {children}
      </motion.section>
    );
  }
);

AnimatedSection.displayName = 'AnimatedSection';
